"use client";

import type { FeatureFlagName, FeatureFlags } from "./types";
import React, { createContext, use, useMemo } from "react";
import { Logger, LogTag, ServiceType } from "@lib/monitoring/logger";

interface FeatureFlagsContextType {
    config: FeatureFlags | null;
    isEnabled: (featureName: FeatureFlagName) => boolean;
}

const FeatureFlagsContext = createContext<FeatureFlagsContextType | undefined>(undefined);

export interface FeatureFlagsProviderProps {
    children: React.ReactNode;
    config: FeatureFlags | null;
}

/**
 * Provider component for feature flags with Vercel Edge Config integration
 * Should be initialized in server component with config fetched from getFeatureFlags()
 */
export const FeatureFlagsProvider = ({ children, config }: FeatureFlagsProviderProps) => {
    const contextValue = useMemo<FeatureFlagsContextType>(
        () => ({
            config,
            isEnabled: (featureName: string) => config?.[featureName]?.enabled ?? false,
        }),
        [config]
    );

    return <FeatureFlagsContext value={contextValue}>{children}</FeatureFlagsContext>;
};

/**
 * Hook to access feature flags context
 * @internal Use useFeatureFlag or useFeatureFlagValue instead
 */
const useFeatureFlagsContext = (): FeatureFlagsContextType => {
    const context = use(FeatureFlagsContext);

    if (!context) {
        throw new Error("Feature flag hooks must be used within a FeatureFlagsProvider");
    }

    return context;
};

export const useFeatureFlags = (): FeatureFlags | null => {
    return useFeatureFlagsContext().config;
};

/**
 * Hook to check if a feature flag is enabled
 * Safe to use - returns false if flag not found or disabled
 *
 * @param featureName - Name of the feature flag to check
 * @returns boolean indicating if feature is enabled (defaults to false)
 *
 * @example
 * ```tsx
 * const isNewCheckoutEnabled = useFeatureFlag("newCheckout");
 *
 * if (isNewCheckoutEnabled) {
 *   return <NewCheckout />;
 * }
 * return <LegacyCheckout />;
 * ```
 */
export const useFeatureFlag = (featureName: FeatureFlagName): boolean => {
    const { isEnabled, config } = useFeatureFlagsContext();
    const enabled = isEnabled(featureName);

    // Log flag access for debugging in development
    if (process.env.NODE_ENV === "development" && config) {
        const flag = config[featureName];
        if (!flag) {
            Logger.warn(ServiceType.WEB, `Feature flag "${featureName}" not found in config`, {
                tag: LogTag.FEATURE_FLAGS,
                featureName,
            });
        }
    }

    return enabled;
};
